import { Person, TableBar } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { sortBy } from 'lodash';
import { useMemo } from 'react';
import { Order, OrderItem } from '../../apiTypes';
import { StyledPaper } from '../../constants/styles';
import { t } from '../../constants/translations';
import { useLocale } from '../../contexts/locale.context';
import KitchenItem from './KitchenItem';

type Props = {
  order: DbDocument<Order>;
  showInfo?: boolean;
  showPBar?: boolean;
  currentTime: number;
};

const KitchenCard = ({ order, showInfo, showPBar, currentTime }: Props) => {
  const { lang } = useLocale();

  const itemsByOrder = useMemo(() => {
    const itemsMap: Record<OrderItem['queue'], OrderItem[]> = { '1': [], '2': [], '3': [] };
    const sortedItems = sortBy(Object.values(order.items), (item) =>
      item.status === 'ordered' ? item.addTime : item.startProcessingTime
    );
    sortedItems.forEach((item) => {
      const key = item.queue || '1';
      itemsMap[key] = [...itemsMap[key], item];
    });
    return itemsMap;
  }, [order.items]);

  const hasQuene = useMemo(() => itemsByOrder['2'].length > 0 || itemsByOrder['3'].length > 0, [itemsByOrder]);

  return (
    <Box sx={{ pb: 1 }}>
      <StyledPaper sx={{ p: 1, pb: 0.5 }}>
        {showInfo && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, mr: 2, ml: 0.5, color: (t) => t.palette.text.secondary }}
            >
              {`№${order.number}`}
            </Typography>
            {order.tableName && <TableBar color="disabled" fontSize="small" />}
            {order.tableName && (
              <Typography variant="body2" sx={{ mr: 2, ml: 0.5, color: (t) => t.palette.text.secondary }}>
                {order.tableName}
              </Typography>
            )}
            <Person color="disabled" fontSize="small" />
            <Typography variant="body2" sx={{ mr: 2, ml: 0.5, color: (t) => t.palette.text.secondary }}>
              {order.workerName}
            </Typography>
          </Box>
        )}

        {Object.entries(itemsByOrder).map(([queue, items]) => (
          <Box key={queue}>
            {hasQuene && items.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 1 }}>
                <Typography variant="subtitle2" color={(t) => t.palette.text.secondary}>
                  {`${t.queue[lang]} ${queue}`}
                </Typography>
              </Box>
            )}
            {items.map((item) => (
              <KitchenItem
                key={item.id}
                item={item}
                order={order}
                currentTime={currentTime}
                showPBar={showPBar}
                showInfo={showInfo}
                disabled={
                  (queue === '2' && itemsByOrder['1'].length > 0) || (queue === '3' && itemsByOrder['2'].length > 0)
                }
              />
            ))}
          </Box>
        ))}
      </StyledPaper>
    </Box>
  );
};

export default KitchenCard;
